import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Transaction } from '@hero/schema';
import { formatDateMain, displayPrice } from '@hero/core';
import { cn } from '@hero/web-lib';
import { useTransactionDrawerStore } from '@hero/hooks-shared';
import {
  CalendarIcon,
  WalletIcon,
  PayIcon,
  TransactionIcon,
} from '@hero/web-icons';
import { Card } from '../../../app/Card/Card';
import { TransactionStatusBadge } from '../Status/TransactionStatusBadge';
import { PriceBadge } from '../../../app/Price/PriceBadge';
import { RefDisplay } from '../../../app';

export interface TransactionCardProps {
  transaction: Transaction;
  displayForUser: 'customer' | 'carrier';
  className?: string;
}

export const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  displayForUser,
  className,
}) => {
  const { t } = useTranslation(['m-payment']);

  const openDrawer = useTransactionDrawerStore(s => s.open);

  const isFailed = transaction.status === 'failed';
  const isCredit = transaction.flow === 1;

  const typeLabel = t(`m-payment:transaction.type.${transaction.type}`);
  const dateStr = transaction.createdAt
    ? formatDateMain({ format: 'date_day', date: transaction.createdAt })
    : undefined;

  const walletBalance =
    transaction.wallet?.balance != null
      ? displayPrice(transaction.wallet.balance, transaction.currency)
      : undefined;

  const isPaidFullyFromWallet = transaction.creditAmount === 0;
  const priceColor = isFailed
    ? 'gray'
    : isCredit
    ? 'green'
    : isPaidFullyFromWallet
    ? 'blue'
    : 'red';

  const pricePrefix = isFailed ? undefined : isCredit ? '+' : undefined;

  return (
    <Card onClick={() => openDrawer(String(transaction._id))} className={className}>
      {/* Row 1: Type label + Status badge */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 min-w-0">
          <div className="flex items-center justify-center rounded-md bg-gray-50 p-1.5 flex-shrink-0">
            <TransactionIcon className="size-3.5 text-gray-400" />
          </div>
          <span className="truncate">{typeLabel}</span>
        </div>

        <TransactionStatusBadge status={transaction.status} />
      </div>

      {/* Row 2: Ref · Date · Payment method */}
      <div className="flex flex-wrap items-center justify-evenly gap-y-1 mt-2">
        <RefDisplay
          refMain={transaction.ref?.main}
          refShort={transaction.ref?.short}
        />

        {dateStr && (
          <>
            <span className="text-gray-300">·</span>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
              <CalendarIcon className="size-3 text-gray-400" />
              <span>{dateStr}</span>
            </div>
          </>
        )}

        {transaction.gateway?.method && (
          <>
            <span className="text-gray-300">·</span>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <PayIcon className="size-3 text-gray-400" />
              <span>
                {t(`m-payment:payment_methods.${transaction.gateway.method}`, {
                  defaultValue: transaction.gateway.method,
                })}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Row 3: Balance + Amount */}
      <div className="flex items-center justify-between mt-2">
        {walletBalance && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <WalletIcon className="size-3 text-gray-400" />
            <span>
              {t('m-payment:transaction.fields.balance')}: {walletBalance}
            </span>
          </div>
        )}

        <PriceBadge
          price={transaction.amount}
          currency={transaction.currency}
          color={priceColor}
          prefix={pricePrefix}
          className={cn(!walletBalance && 'ms-auto')}
        />
      </div>
    </Card>
  );
};
